import { faRotate } from '@fortawesome/pro-solid-svg-icons';
import { canvasUnitsPerPixel } from '@/utils/canvasUnits';

const [ICON_VIEWBOX_WIDTH, ICON_VIEWBOX_HEIGHT, , , ICON_PATH_DATA] = faRotate.icon;
const ICON_GLYPH = new Path2D(ICON_PATH_DATA);

const ICON_SIZE_PX = 28;
const ICON_RADIUS_PX = 8;
const ICON_PADDING_PX = 5;
const ICON_BORDER_WIDTH_PX = 1;
const ICON_BORDER_COLOR = '#E6E6E6';
const ICON_BACKGROUND = '#FFF';
const ICON_COLOR = '#333';
const ICON_SHADOW_COLOR = 'rgba(0, 0, 0, 0.15)';
const ICON_SHADOW_BLUR_PX = 16;

const BADGE_FONT_SIZE_PX = 14;
const BADGE_PADDING_X_PX = 5;
const BADGE_PADDING_Y_PX = 5;
const BADGE_GAP_PX = 10;
const BADGE_BACKGROUND = 'rgba(13,3,36,1)';
const BADGE_TEXT_COLOR = '#ffffff';

/**
 * Live angle readout drawn next to the rotation control. Kept out of the icon's rotated transform
 *  so the text stays horizontal regardless of the object's angle.
 * @param {CanvasRenderingContext2D} ctx Context to render on
 * @param {Number} left x coordinate of the control center
 * @param {Number} top y coordinate of the control center
 * @param {Number} size rendered size of the rotation icon
 * @param {Number} angle current angle of the object being rotated
 */
const renderAngleBadge = (ctx, left, top, size, angle, px) => {
    // Round before wrapping, otherwise a value like 359.6 rounds up and renders as "360°"
    const label = `${((Math.round(angle) % 360) + 360) % 360}°`;

    ctx.save();
    // drawControls() applies cornerDashArray to the ctx before calling each control's render
    ctx.setLineDash([]);
    ctx.font = `600 ${px(BADGE_FONT_SIZE_PX)}px Helvetica, Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const width = ctx.measureText(label).width + px(BADGE_PADDING_X_PX) * 2;
    const height = px(BADGE_FONT_SIZE_PX + BADGE_PADDING_Y_PX * 2);
    const above = top - size / 2 - px(BADGE_GAP_PX) - height;
    // Flip below the icon when there's no room above, otherwise the canvas element clips the badge
    const boxTop = above > 0 ? above : top + size / 2 + px(BADGE_GAP_PX);

    ctx.beginPath();
    ctx.roundRect(left - width / 2, boxTop, width, height, height / 3);
    ctx.fillStyle = BADGE_BACKGROUND;
    ctx.fill();

    ctx.fillStyle = BADGE_TEXT_COLOR;
    ctx.fillText(label, left, boxTop + height / 2);
    ctx.restore();
};

/**
 * Replaces fabric's default circle for the rotation control with a Font Awesome glyph. The glyph is
 *  drawn from raw path data rather than an image so it needs no async load and stays crisp at any
 *  cornerSize / device pixel ratio.
 * @param {CanvasRenderingContext2D} ctx Context to render on
 * @param {Number} left x coordinate where the control center should be
 * @param {Number} top y coordinate where the control center should be
 * @param {Object} styleOverride override for fabric.Object controls style
 * @param {fabric.Object} fabricObject the object the control belongs to
 */
function renderRotationControl(ctx, left, top, styleOverride, fabricObject) {
    const unitsPerPixel = canvasUnitsPerPixel(fabricObject.canvas);
    const px = (value) => value * unitsPerPixel;
    const size = px(ICON_SIZE_PX);
    const border = px(ICON_BORDER_WIDTH_PX);

    ctx.save();
    ctx.setLineDash([]);
    ctx.translate(left, top);
    // Follow the object's rotation, same as fabric's renderSquareControl
    ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));

    // Stroke centers on the path, so the box is inset by half a border to keep it inside `size`
    const boxSize = size - border;
    ctx.beginPath();
    ctx.roundRect(-boxSize / 2, -boxSize / 2, boxSize, boxSize, px(ICON_RADIUS_PX) - border / 2);
    ctx.fillStyle = ICON_BACKGROUND;
    ctx.shadowColor = ICON_SHADOW_COLOR;
    ctx.shadowBlur = px(ICON_SHADOW_BLUR_PX);
    // Set explicitly rather than relying on the default: fabric's own shadow rendering writes these
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.fill();
    // Only the container casts the shadow; the border and glyph would otherwise smear it
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.lineWidth = border;
    ctx.strokeStyle = ICON_BORDER_COLOR;
    ctx.stroke();

    // CSS box model: the glyph gets whatever the border and padding leave behind. faRotate's ink
    //  fills its 512 viewBox edge to edge, so this size is what actually shows up.
    const glyphSize = size - px(ICON_BORDER_WIDTH_PX + ICON_PADDING_PX) * 2;
    ctx.scale(glyphSize / ICON_VIEWBOX_WIDTH, glyphSize / ICON_VIEWBOX_HEIGHT);
    ctx.translate(-ICON_VIEWBOX_WIDTH / 2, -ICON_VIEWBOX_HEIGHT / 2);
    ctx.fillStyle = ICON_COLOR;
    ctx.fill(ICON_GLYPH);
    ctx.restore();

    const transform = fabricObject.canvas && fabricObject.canvas._currentTransform;
    // _transformObject() requests a render on every angle change, so this redraws in step with the drag
    if (transform && transform.action === 'rotate' && transform.target === fabricObject) {
        renderAngleBadge(ctx, left, top, size, fabricObject.angle, px);
    }
}

// Mutated in place: fabric.Textbox and every custom class in libs/ share this exact Control instance
//  by reference (they spread fabric.Object.prototype.controls), so replacing the entry would leave
//  them on the old control
const rotationControl = fabric.Object.prototype.controls.mtr;

rotationControl.render = renderRotationControl;

// rotationStyleHandler returns control.cursorStyle verbatim, so overriding the property keeps
//  fabric's own 'not-allowed' for lockRotation
rotationControl.cursorStyle = 'grab';

// calcCornerCoords only receives the object's cornerSize, which says nothing about the chip's screen
//  size, so _setCornerCoords stashes the converted size immediately before fabric rebuilds the boxes
const originalSetCornerCoords = fabric.Object.prototype._setCornerCoords;
fabric.Object.prototype._setCornerCoords = function () {
    rotationControl.__hitSize = ICON_SIZE_PX * canvasUnitsPerPixel(this.canvas);
    return originalSetCornerCoords.call(this);
};

rotationControl.calcCornerCoords = function (objectAngle, objectCornerSize, centerX, centerY, isTouch) {
    return fabric.Control.prototype.calcCornerCoords.call(
        this,
        objectAngle,
        // touch keeps fabric's own larger touchCornerSize
        isTouch ? objectCornerSize : this.__hitSize || objectCornerSize,
        centerX,
        centerY,
        isTouch
    );
};

const originalSetupCurrentTransform = fabric.Canvas.prototype._setupCurrentTransform;
fabric.Canvas.prototype._setupCurrentTransform = function (e, target, alreadySelected) {
    originalSetupCurrentTransform.call(this, e, target, alreadySelected);

    // fabric never recomputes the cursor while a transform is running (__onMouseMove goes straight to
    //  _transformObject), so setting it once here holds for the whole drag; __onMouseUp restores it
    //  through _setCursorFromEvent
    const transform = this._currentTransform;
    if (transform && transform.target === target && transform.action === 'rotate') {
        this.setCursor('grabbing');
    }
};
